import {
    ExcelSheet,
    indicatorRows,
    money,
    percent
} from '../StatisticsExcel.Functions';
import * as Functions from './BillingControl.Functions';

/**
 * Exportación del control de facturación.
 *
 * El objetivo es que el administrativo pueda trabajar el archivo: cada control de
 * fuga baja en su propia hoja con todos los casos, no sólo los primeros que entran
 * en pantalla, y la matriz va en formato largo para poder hacer tabla dinámica.
 */

/** El circuito paso a paso, más el estado de la plata cargada. */
export const funnelSheets = (funnel: Functions.BillingFunnel): ExcelSheet[] => [
    {
        name: 'Circuito',
        description: 'De lo pactado a lo cobrado, con lo que se cae en cada eslabón.',
        rows: funnel.steps.map((step) => ({
            Eslabón: step.label,
            'Qué es': step.description,
            Casos: step.count,
            Importe: step.amount === undefined ? '' : money(step.amount),
            'Casos que se caen acá': step.dropCount ?? '',
            'Importe que se cae acá': step.dropAmount === undefined ? '' : money(step.dropAmount)
        }))
    },
    {
        name: 'Estado de lo cargado',
        description: 'En qué punto de la liquidación está la plata ya cargada.',
        rows: indicatorRows([
            ['Total cargado', money(funnel.totals.total), `${funnel.totals.consumptions} consumos`],
            ['Sin liquidar', money(funnel.totals.unsettled), 'Todavía no entró en ninguna liquidación'],
            ['En liquidación, sin cobrar', money(funnel.totals.inSettlement)],
            ['Cobrado', money(funnel.totals.collected)],
            ['Fuera de liquidación', money(funnel.totals.excluded), 'Rechazados o ignorados']
        ])
    }
];

/** La matriz en formato largo: una fila por concepto y visita con movimiento. */
export const matrixSheets = (matrix: Functions.BillingMatrix): ExcelSheet[] => {
    const rows = matrix.rows.flatMap((row) =>
        Array.from(row.cells.entries()).map(([columnKey, cell]) => ({
            Concepto: row.label,
            'Pactado en el contrato': row.isContracted ? 'Sí' : 'No',
            Visita: matrix.columns.find((column) => column.key === columnKey)?.label ?? columnKey,
            Importe: money(cell.amount),
            Consumos: cell.count,
            Pacientes: cell.patients,
            '% del total': percent(
                matrix.total.amount > 0 ? Math.round((cell.amount / matrix.total.amount) * 1000) / 10 : 0
            )
        }))
    );

    return [
        {
            name: 'Cobros por concepto y visita',
            description: 'Una fila por casillero con movimiento, para tabla dinámica.',
            rows
        },
        {
            name: 'Totales por concepto',
            description: 'Incluye los conceptos pactados que nunca se cobraron.',
            rows: matrix.rows.map((row) => ({
                Concepto: row.label,
                'Pactado en el contrato': row.isContracted ? 'Sí' : 'No',
                'Revisado en el contrato': row.isContracted ? (row.isApproved === false ? 'No' : 'Sí') : '',
                Importe: money(row.total.amount),
                Consumos: row.total.count,
                'Visitas en las que se cobró': row.cells.size
            }))
        },
        {
            name: 'Totales por visita',
            rows: matrix.columns.map((column) => ({
                Visita: column.label,
                Opcional: column.isOptional ? 'Sí' : 'No',
                Importe: money(column.total.amount),
                Consumos: column.total.count
            }))
        }
    ];
};

/** Un resumen de los cinco controles, y después una hoja por control con todos los casos. */
export const leakSheets = (groups: Functions.LeakGroup[]): ExcelSheet[] => {
    const totals = Functions.leakTotals(groups);

    const summary: ExcelSheet = {
        name: 'Controles',
        description: 'Resumen de los controles de fuga sobre el contrato.',
        note: 'Los importes marcados como estimados no salen de un consumo cargado: se calculan con lo que esa misma visita facturó en promedio por paciente.',
        rows: [
            ...groups.map((group) => ({
                Control: group.title,
                Pregunta: group.question,
                Casos: group.count,
                Importe: money(group.amount),
                Estimado: group.isEstimated ? 'Sí' : 'No',
                'Qué hacer': group.action
            })),
            {
                Control: 'TOTAL',
                Pregunta: '',
                Casos: totals.cases,
                Importe: money(totals.certain),
                Estimado: '',
                'Qué hacer': `Además, ${Math.round(totals.estimated)} estimados en visitas sin consumo`
            }
        ]
    };

    const detail = groups
        .filter((group) => group.count > 0)
        .map((group): ExcelSheet => ({
            name: group.title,
            description: group.question,
            rows: group.items.map((item) => ({
                Caso: item.title,
                Detalle: item.detail ?? '',
                Importe: item.amount === undefined ? '' : money(item.amount),
                Estimado: item.isEstimated ? 'Sí' : 'No',
                'Id del consumo': item.consumptionId ?? ''
            }))
        }));

    return [summary, ...detail];
};

/** Todo el control de facturación en un solo libro. */
export const billingControlSheets = (
    funnel: Functions.BillingFunnel,
    matrix: Functions.BillingMatrix,
    groups: Functions.LeakGroup[]
): ExcelSheet[] => [...funnelSheets(funnel), ...matrixSheets(matrix), ...leakSheets(groups)];
